// src/components/RichTextWithDefinitions.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import RichText from './RichText.tsx';
import { supabase } from '../supabaseClient.ts';

interface Props {
  html: string;
  className?: string;
  companyId?: string | null;
}

type Definition = {
  word: string;
  definition: string;
};

const RichTextWithDefinitions: React.FC<Props> = ({ html, className, companyId }) => {
  const [definitions, setDefinitions] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Tooltip click handler (used for each span)
  const handleWordClick = useCallback((e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const wordEl = e.target as HTMLElement;
    const definition = wordEl.getAttribute('data-definition') || '';
    if (!definition) return;

    // Remove existing tooltip if any
    const existing = document.querySelector('.definition-tooltip-popup');
    if (existing) {
      existing.remove();
      return;
    }

    // Create tooltip element (just definition text)
    const tooltip = document.createElement('div');
    tooltip.className = 'definition-tooltip-popup';
    tooltip.style.cssText = `
      position: fixed;
      background: white;
      border: 1px solid #ccc;
      border-radius: 8px;
      padding: 12px 16px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      max-width: 300px;
      font-size: 14px;
      line-height: 1.5;
      pointer-events: auto;
    `;

    const defText = document.createElement('div');
    defText.style.cssText = 'color: #333;';
    defText.textContent = definition;

    tooltip.appendChild(defText);

    // Position tooltip near the clicked word
    const rect = wordEl.getBoundingClientRect();
    const tooltipRect = { width: 300, height: 100 }; // approximate
    let left = rect.left;
    let top = rect.bottom + 8;

    // Adjust if tooltip would go off screen
    if (left + tooltipRect.width > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width - 16;
    }
    if (top + tooltipRect.height > window.innerHeight) {
      top = rect.top - tooltipRect.height - 8;
    }

    tooltip.style.left = `${Math.max(16, left)}px`;
    tooltip.style.top = `${Math.max(16, top)}px`;

    document.body.appendChild(tooltip);

    // Close on click outside
    const closeTooltip = (event: MouseEvent) => {
      if (!tooltip.contains(event.target as Node)) {
        tooltip.remove();
        document.removeEventListener('click', closeTooltip);
        document.removeEventListener('scroll', closeOnScroll, true);
      }
    };

    // Close on scroll
    const closeOnScroll = () => {
      tooltip.remove();
      document.removeEventListener('click', closeTooltip);
      document.removeEventListener('scroll', closeOnScroll, true);
    };

    setTimeout(() => {
      document.addEventListener('click', closeTooltip);
      document.addEventListener('scroll', closeOnScroll, true);
    }, 0);
  }, []);

  // Load definitions for the company (or global if no company)
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        let query = supabase.from('definitions').select('word, definition');

        if (companyId) {
          query = query.or(`company_id.is.null,company_id.eq.${companyId}`);
        } else {
          query = query.is('company_id', null);
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error loading definitions:', error);
          setDefinitions(new Map());
        } else {
          const defMap = new Map<string, string>();
          (data ?? []).forEach((d: Definition) => {
            // Store lowercase for case-insensitive matching
            const lowerWord = d.word.toLowerCase().trim();
            if (!defMap.has(lowerWord)) {
              defMap.set(lowerWord, d.definition);
            }
          });
          setDefinitions(defMap);
        }
      } catch (err) {
        console.error('Error loading definitions:', err);
        setDefinitions(new Map());
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  // Process HTML to add definition highlights after RichText renders
  useEffect(() => {
    if (!containerRef.current || loading || definitions.size === 0) {
      return;
    }

    // Wait for RichText to finish rendering (KaTeX processing)
    const timeout = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;

      // Function to process text nodes and wrap defined words
      const processNode = (node: Node): void => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          const parent = node.parentElement;

          if (!parent || parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') {
            return;
          }

          // Skip if inside KaTeX elements or already processed definition words
          if (parent.closest('.katex') || parent.classList.contains('definition-word')) {
            return;
          }

          // Split by word boundaries (words are sequences of letters)
          const wordRegex = /\b([a-zA-Z]+(?:'[a-zA-Z]+)?)\b/g;
          const parts: Array<{ type: 'text' | 'word'; content: string }> = [];
          let lastIndex = 0;
          let match: RegExpExecArray | null;

          while ((match = wordRegex.exec(text)) !== null) {
            // Add text before the word
            if (match.index > lastIndex) {
              parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
            }
            // Add the word
            parts.push({ type: 'word', content: match[1] });
            lastIndex = match.index + match[0].length;
          }
          // Add remaining text
          if (lastIndex < text.length) {
            parts.push({ type: 'text', content: text.slice(lastIndex) });
          }

          if (parts.length <= 1) {
            // No words to process
            return;
          }

          // Create fragment with processed content
          const fragment = document.createDocumentFragment();

          parts.forEach((part) => {
            if (part.type === 'text') {
              fragment.appendChild(document.createTextNode(part.content));
            } else {
              const lowerWord = part.content.toLowerCase();
              const definition = definitions.get(lowerWord);

              if (definition) {
                // Word has a definition - wrap in span and attach click handler
                const span = document.createElement('span');
                span.className = 'definition-word';
                span.textContent = part.content;
                span.setAttribute('data-definition', definition);
                span.setAttribute('data-word', part.content);
                span.addEventListener('click', handleWordClick);
                fragment.appendChild(span);
              } else {
                fragment.appendChild(document.createTextNode(part.content));
              }
            }
          });

          // Replace the text node
          if (fragment.childNodes.length > 0) {
            parent.replaceChild(fragment, node);
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          // Skip if already processed or is a math element
          if (
            element.classList.contains('definition-word') ||
            element.classList.contains('katex') ||
            element.closest('.katex')
          ) {
            return;
          }

          // Process children (use Array.from to avoid live collection issues)
          const children = Array.from(element.childNodes);
          children.forEach((child) => processNode(child));
        }
      };

      // Process all nodes in the container
      Array.from(container.childNodes).forEach((node) => processNode(node));
    }, 100); // Small delay to let KaTeX finish

    return () => clearTimeout(timeout);
  }, [html, definitions, loading, handleWordClick]);

  return (
    <>
      <style>
        {`
          .definition-word {
            border-bottom: 2px solid #1976d2;
            cursor: help;
            transition: background-color 0.15s ease;
            text-decoration: none;
          }
          .definition-word:hover {
            background-color: rgba(25, 118, 210, 0.08);
          }
        `}
      </style>
      <div ref={containerRef}>
        <RichText html={html} className={className} />
      </div>
    </>
  );
};

export default RichTextWithDefinitions;
